/**
 * Cuidei — pharmacy-based video consultation platform.
 *
 * Generalist medical video consultation sold inside partner pharmacies: patient
 * scans a QR code → lands here with a pharmacy_id → pays R$79 → enters a live
 * waiting queue → talks to a doctor by video → (if applicable) receives a Memed
 * prescription/exam request delivered to their e-mail and to the
 * partner pharmacy's email. The pharmacy earns a fixed commission per completed
 * consultation. See the MVP spec for the full model.
 */

/** Initial consultation price (BRL). */
export const CUIDEI_CONSULTATION_PRICE = 79

/** Pharmacy commission per completed consultation (BRL). */
export const CUIDEI_PHARMACY_COMMISSION = 10

/** Brand cookie that marks a session as a Cuidei session (mirrors isMedleveAddress / isIshaAddress). */
export const CUIDEI_BRAND_COOKIE = 'isCuideiAddress'

/** Cookie that persists the pharmacy attribution across the whole flow. */
export const CUIDEI_ATTRIBUTION_COOKIE = 'cuideiAttribution'

/** Pathology landing slug of the current flow, so every tracking event can be attributed per pathology. */
export const CUIDEI_PATHOLOGY_COOKIE = 'cuideiPathology'

/** partnerName on the patient profile once an account is created in the Cuidei space. */
export const CUIDEI_PARTNER_NAME = 'cuidei'

/** How long attribution persists (hours). A QR scan → completed consult should comfortably fit. */
export const CUIDEI_ATTRIBUTION_TTL_HOURS = 12

/**
 * Routes for the Cuidei space. The pharmacy (QR) flow lives under /farma since
 * 2026-10; pathology landings live at the site root (cuidei.com.br/<slug>) and
 * are served by a host-scoped fallback rewrite in next.config.js.
 */
export const CUIDEI_ROUTES = {
  landing: '/farma',
  flow: '/farma/consulta',
  login: '/farma/entrar',
  /** Logged-in patient area: the live consultation (video). */
  consultations: '/farma/consultas',
  /** Logged-in patient area: prescriptions + exam requests issued by the doctor. */
  prescriptions: '/farma/receitas',
  /** Logged-in patient area: account details. */
  account: '/farma/conta',
  pharmacyEntry: (pharmacyId: string) => `/farma/p/${pharmacyId}`,
  pathologyLanding: (slug: string) => `/${slug}`,
  pathologyFlow: (slug: string) => `/${slug}/consulta`
} as const

/** Ordered patient-flow steps (mirrors the Lovable mock: "Passo X de 4"). */
export enum CuideiStep {
  /** Pathology flow only, first step: birth date, prescription sex, height, weight + the pathology question. */
  Questionario = 'questionario',
  /** /farma: full patient data. Pathology flow: only name, e-mail, phone and CPF (what payment needs). */
  Dados = 'dados',
  Pagamento = 'pagamento',
  /** Pathology flow only, after payment: identity document, address and the account password. */
  Finalizar = 'finalizar',
  FilaVideo = 'fila_video',
  Consentimento = 'consentimento',
  Conta = 'conta',
  Feedback = 'feedback'
}

/** The 4 steps surfaced in the progress bar (account + feedback are post-flow). */
export const CUIDEI_PROGRESS_STEPS: CuideiStep[] = [
  CuideiStep.Dados,
  CuideiStep.Pagamento,
  CuideiStep.FilaVideo,
  CuideiStep.Consentimento
]

/**
 * Progress-bar steps. Pathology flows ask the fewest fields before paying:
 * questionnaire → basic data → payment → the rest of the data + password → queue.
 */
export const CUIDEI_PATHOLOGY_PROGRESS_STEPS: CuideiStep[] = [
  CuideiStep.Questionario,
  CuideiStep.Dados,
  CuideiStep.Pagamento,
  CuideiStep.Finalizar,
  CuideiStep.FilaVideo,
  CuideiStep.Consentimento
]

export const cuideiProgressSteps = (hasPathology: boolean): CuideiStep[] =>
  hasPathology ? CUIDEI_PATHOLOGY_PROGRESS_STEPS : CUIDEI_PROGRESS_STEPS

/**
 * Versioned consent text for the single combined document-delivery consent.
 * Stored alongside the consultation for LGPD / medical-privacy auditing.
 */
export const CUIDEI_CONSENT_TEXT_VERSION = '2026-06-v2'
export const CUIDEI_CONSENT_TEXT =
  'Autorizo o envio da minha receita e dos pedidos de exame por e-mail e para a farmácia parceira.'
export const CUIDEI_EMAIL_ONLY_CONSENT_TEXT_VERSION = '2026-07-email-v1'
export const CUIDEI_EMAIL_ONLY_CONSENT_TEXT =
  'Autorizo o envio da minha receita e dos pedidos de exame para o meu e-mail.'

/** Consultation outcomes the doctor can select (mirrors backend enum). */
export enum CuideiConsultationOutcome {
  PrescriptionIssued = 'prescription_issued',
  ExamRequestIssued = 'exam_request_issued',
  MedicalOrientationWithoutPrescription = 'medical_orientation_without_prescription',
  ReferredToInPersonCare = 'referred_to_in_person_care',
  EmergencyOrientation = 'emergency_orientation',
  NoDocumentNeeded = 'no_document_needed',
  InterruptedConsultation = 'interrupted_consultation',
  Other = 'other'
}

/** Tracking event names (spec §27). Emitted via trackCuideiEvent. */
export const CUIDEI_EVENTS = {
  qrLandingView: 'qr_landing_view',
  pathologyLandingView: 'pathology_landing_view',
  landingCtaClicked: 'landing_cta_clicked',
  patientFormStarted: 'patient_form_started',
  patientFormCompleted: 'patient_form_completed',
  cepSubmitted: 'cep_submitted',
  cepResolved: 'cep_resolved',
  cepResolutionFailed: 'cep_resolution_failed',
  paymentStarted: 'payment_started',
  paymentApproved: 'payment_approved',
  paymentFailed: 'payment_failed',
  /** Paid order; the Cuidei GTM sends it to GA4 / Google Ads as the purchase conversion. */
  purchase: 'purchase',
  queueStarted: 'queue_started',
  videoStarted: 'video_started',
  videoEnded: 'video_ended',
  videoWrapUpShown: 'video_wrap_up_shown',
  videoInitializationFailed: 'video_initialization_failed',
  videoPublisherRecovered: 'video_publisher_recovered',
  documentDeliveryConsentDisplayed: 'document_delivery_consent_displayed',
  documentDeliveryAuthorized: 'document_delivery_authorized',
  documentDeliveryNotAuthorized: 'document_delivery_not_authorized',
  patientFeedbackSubmitted: 'patient_feedback_submitted',
  consultationCompleted: 'consultation_completed'
} as const
